import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { AddCartFormProps, AddCartFormVO } from './type'

import type { CartUpdRequest } from '@/api/@types'
import {
  ItemErrorLabel,
  ItemOrderUnitFormField,
  ItemPrice,
  ItemThumbnailBannar,
} from '@/components/features/item'
import { AddShoppingCartFilledIcon } from '@/components/icons/AddShoppingCartFilled'
import { Icon } from '@/components/ui'
import { Button, ErrorMessageList } from '@/components/ui'
import { useCart, useUpdateCart } from '@/hooks/features/cart'
import { useNotification } from '@/hooks/util'
import { useSalesTypesMaster } from '@/stores'
import { cartItemReducer, extendFieldNames } from '@/utils/features/cart'
import { getErrorMessages } from '@/utils/text'

import { schema } from './const'

export const AddCartForm = ({
  items,
  onSubmit,
  onClickCancel,
}: AddCartFormProps) => {
  const { t } = useTranslation()
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const { notify } = useNotification()

  const { salesTypesMaster } = useSalesTypesMaster()

  const cart = useCart({
    // config: {
    //   enabled: false,
    // },
  })
  const updateCart = useUpdateCart({})

  const { register, control, handleSubmit, reset, formState } =
    useForm<AddCartFormVO>({
      resolver: schema && zodResolver(schema),
      mode: 'onBlur',
      defaultValues: {
        items: items.map((item) => ({
          itemCode: item.itemCode,
          salesTypeQuantities: [
            {
              salesType: salesTypesMaster[0].salesTypeCode,
              quantity: 0,
              note: '',
            },
          ],
        })),
      },
    })

  const isLoading =
    cart.isLoading || updateCart.isLoading || formState.isSubmitting

  const inputItems = useWatch({ control, name: 'items' })

  const totalItemCount = useMemo(() => {
    return inputItems.reduce((totalCount, item) => {
      const quantity = item.salesTypeQuantities.reduce(
        (sum, salesTypeQuantity) => (sum += salesTypeQuantity.quantity),
        0
      )
      return totalCount + quantity
    }, 0)
  }, [inputItems])

  const submitHandler = async (input: AddCartFormVO) => {
    try {
      if (errorMessages.length > 0) {
        setErrorMessages([])
      }

      const currentCart = await cart.refetch()

      // 既にカートに入っている商品
      const currentCartItems =
        currentCart.data?.cartItems.map((item) => ({
          itemCode: item.item.itemCode,
          salesTypeQuantities: item.salesTypeQuantities,
        })) ?? []

      // 追加する商品
      const inputCartItems = input.items.map((item) => ({
        itemCode: item.itemCode,
        salesTypeQuantities: item.salesTypeQuantities.filter(
          ({ quantity }) => quantity > 0
        ),
      }))

      // カートに入る商品
      const cartItems: CartUpdRequest['cartItems'] = [
        ...currentCartItems,
        ...inputCartItems,
      ].filter((item) => item.salesTypeQuantities.length > 0)

      await updateCart.mutateAsync(
        {
          data: {
            cartItems: cartItems.reduce(cartItemReducer, []),
          },
        },
        {
          onSuccess: () => {
            reset()
            onSubmit && onSubmit()
            notify({
              id: 'add-cart-success',
              type: 'success',
              message: `${t('item.cart.title')}${t('item.cart.addSuccess')}`,
            })
          },
          onError: (error) => {
            if (error.response?.status === 400) {
              const extendedFieldNames = extendFieldNames({
                fields: error.response.data.fields,
              })
              setErrorMessages(
                getErrorMessages({
                  fields: error.response.data.fields,
                  fieldNames: extendedFieldNames,
                })
              )
            }
          },
        }
      )
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(error)
      }
    }
  }

  return (
    <div id="item-add-modal-form">
      {errorMessages.length > 0 && (
        <div className={clsx('tw-mb-2 tw-text-center')}>
          <ErrorMessageList errorMessages={errorMessages} />
        </div>
      )}

      <ul
        className={clsx(
          'tw-flex tw-max-h-[60vh] tw-flex-col tw-space-y-2 tw-overflow-y-auto tw-pb-20'
        )}
      >
        {inputItems.map((inputItem, itemIndex) => {
          const item = items.find((i) => i.itemCode === inputItem.itemCode)
          return (
            item && (
              <li
                key={item.itemCode}
                className={clsx(
                  'tw-mb-2',
                  'tw-border-b tw-border-gray-300',
                  'last:tw-border-b-0'
                )}
              >
                <div className={clsx('tw-flex tw-flex-col', 'tw-gap-2')}>
                  <ItemThumbnailBannar
                    item={item}
                    nameFooter={
                      <>
                        <ItemErrorLabel status={item.itemStatus} />
                      </>
                    }
                  />
                  <ItemPrice
                    item={item}
                    cartSalesTypeQuantities={inputItem.salesTypeQuantities}
                    direction={'vertical'}
                  />
                </div>

                <div className={clsx('tw-mt-4')}>
                  <ItemOrderUnitFormField
                    control={control}
                    itemIndex={itemIndex}
                    register={register}
                  />
                </div>
              </li>
            )
          )
        })}
      </ul>
      <div
        className={clsx(
          'tw-absolute tw-inset-x-0 tw-bottom-0 tw-py-4',
          'tw-flex tw-items-center tw-justify-center tw-space-x-2 tw-bg-white tw-px-2 tw-shadow-inner'
        )}
      >
        <div className={clsx('tw-w-full')}>
          <Button
            type="button"
            variant="secondary"
            disabled={isLoading}
            onClick={onClickCancel}
          >
            {t('item.cart.close')}
          </Button>
        </div>
        <div className={clsx('tw-w-full tw-min-w-[184px]')}>
          <Button
            disabled={isLoading || totalItemCount <= 0}
            isLoading={isLoading}
            startIcon={
              <Icon
                icon={AddShoppingCartFilledIcon}
                size={'lg'}
                color={'white'}
              />
            }
            onClick={handleSubmit(submitHandler)}
          >
            {t('item.cart.addToCart')}
          </Button>
        </div>
      </div>
    </div>
  )
}
